import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getBlogBySlug } from '../services/blog.service';
import SEOHead from '../../../components/SEO/SEOHead';
import { blogDocumentMeta, formatBlogDate } from '../../../components/SEO/blogMeta';
import { publicCanonical } from '../../../components/SEO/siteUrl';
import { getBlogStructuredData } from '../../../components/SEO/structuredData';
import { blogFaqs } from '../../../components/SEO/blogFaqs';
import { readPrerenderState } from '../prerenderState';

function RelatedCard({ blog }) {
  return (
    <Link to={`/blogs/${blog.slug}`} className="group flex gap-3 items-center hover:bg-gray-50 rounded-xl p-3 transition-colors min-h-12 md:min-h-0">
      {blog.coverImageUrl ? (
        <img src={blog.coverImageUrl} alt={blog.title} className="h-16 w-24 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="h-16 w-24 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center text-2xl">📝</div>
      )}
      <div className="min-w-0">
        <p className="text-base md:text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">{blog.title}</p>
        <p className="text-xs text-gray-400 mt-1 capitalize">{blog.category?.replace(/-/g, ' ')}</p>
      </div>
    </Link>
  );
}

function seededPost(slug) {
  const post = readPrerenderState()?.post;
  if (post && post.blog && post.blog.slug === slug) return post;
  return null;
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const seeded = seededPost(slug);
  const [blog, setBlog] = useState(seeded ? seeded.blog : null);
  const [related, setRelated] = useState(seeded ? seeded.related || [] : []);
  const [loading, setLoading] = useState(!seeded);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const activeRequest = useRef(0);

  useEffect(() => {
    const request = ++activeRequest.current;
    const background = Boolean(seeded && seeded.blog.slug === slug);
    if (!background) {
      setLoading(true);
      setNotFound(false);
      setError(null);
    }
    getBlogBySlug(slug)
      .then((res) => {
        if (request !== activeRequest.current) return;
        setBlog(res.blog);
        setRelated(res.related || []);
        setNotFound(false);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (request !== activeRequest.current) return;
        const missing = err?.response?.status === 404 || err?.statusCode === 404 || err?.status === 404;
        if (missing) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        if (background) return;
        setError('Something went wrong loading this blog. Please try again.');
        setLoading(false);
      });
  }, [slug, seeded]);

  if (loading) {
    return (
      <>
        <SEOHead
          title="Blog | Fixome"
          description="Loading article from Fixome home services blog."
          url={publicCanonical(`/blogs/${slug || ''}`)}
          canonical={publicCanonical(`/blogs/${slug || ''}`)}
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" />
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <SEOHead
          title="Article not found | Fixome Blog"
          description="This blog post could not be found."
        url={publicCanonical(`/blogs/${slug || ''}`)}
        canonical={publicCanonical(`/blogs/${slug || ''}`)}
        noindex
        />
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <div className="text-6xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Blog not found</h1>
        <p className="text-gray-500 mb-6">The blog you're looking for doesn't exist or has been removed.</p>
        <Link to="/blogs" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
          ← Back to Blogs
        </Link>
      </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEOHead
          title="Error | Fixome Blog"
          description="Could not load this article."
          url={publicCanonical(`/blogs/${slug || ''}`)}
          canonical={publicCanonical(`/blogs/${slug || ''}`)}
          noindex
        />
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Failed to load blog</h1>
        <p className="text-gray-500 mb-6">{error}</p>
        <Link to="/blogs" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
          ← Back to Blogs
        </Link>
      </div>
      </>
    );
  }

  if (!blog) return null;

  const publishDate = formatBlogDate(blog.publishedAt, 'long');

  const publishedIso = blog.publishedAt ? new Date(blog.publishedAt).toISOString() : undefined;
  const modifiedIso = blog.updatedAt ? new Date(blog.updatedAt).toISOString() : publishedIso;
  const coverForOg = blog.coverImageUrl || '/fixome_logo.png';
  const meta = blogDocumentMeta(blog, slug);

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={`${blog.category || 'home services'}, Kerala home services, Fixome`}
        image={coverForOg}
        url={publicCanonical(`/blogs/${slug}`)}
        canonical={publicCanonical(`/blogs/${slug}`)}
        type="article"
        publishedTime={publishedIso}
        modifiedTime={modifiedIso}
        structuredData={getBlogStructuredData({ ...blog, description: meta.description }, slug, blogFaqs(slug))}
      />
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <nav className="text-base md:text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to="/blogs" className="inline-flex items-center justify-center min-h-12 md:min-h-0 min-w-12 md:min-w-0 hover:text-blue-600">Blog</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700 capitalize">{blog.category?.replace(/-/g, ' ')}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Article */}
          <article className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {blog.coverImageUrl && (
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={blog.coverImageUrl}
                    alt={blog.title}
                    width={blog.coverWidth || 1200}
                    height={blog.coverHeight || 675}
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full capitalize">
                    {blog.category?.replace(/-/g, ' ')}
                  </span>
                  {publishDate && <span className="text-xs text-gray-400">{publishDate}</span>}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-tight">
                  {blog.title}
                </h1>
                {/* Rendered Markdown content */}
                <div className="prose prose-gray max-w-none text-gray-700 text-base">
                  <ReactMarkdown components={{ h1: 'h2' }}>{blog.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Back link */}
            <Link to="/blogs" className="inline-flex items-center gap-2 min-h-12 md:min-h-0 text-base md:text-sm font-medium text-blue-600 hover:underline">
              ← All Blogs
            </Link>

            {/* Related */}
            {related.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Related Articles</h3>
                <div className="space-y-2">
                  {related.map(r => <RelatedCard key={r._id} blog={r} />)}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white text-center">
              <div className="text-2xl mb-2">🔧</div>
              <h3 className="font-bold text-base mb-1">Need a pro?</h3>
              <p className="text-blue-100 text-xs mb-4">Book a certified technician in minutes.</p>
              <Link to="/" className="inline-flex items-center justify-center min-h-12 md:min-h-0 w-full bg-white text-blue-700 font-semibold text-base md:text-sm rounded-lg hover:bg-blue-50 transition-colors">
                Book Now
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
    </>
  );
}
